/**
 * Formatting for reporting figures.
 *
 * SHARED, because a dashboard whose tiles and axes round differently looks broken even when it is
 * right — and because "minor units to a readable amount" is the one conversion that must happen in
 * exactly one place. Everything arrives as an integer: money in minor units, rates in basis points.
 */

/**
 * Money for a tile or a tooltip — full precision, grouped.
 */
export function formatMoney(minor: number, currency: string): string {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(minor / 100);
}

/**
 * Money for an axis — abbreviated, because "KES 1,234,567" repeated six times down a y-axis is a
 * wall of digits nobody reads.
 */
export function formatMoneyCompact(minor: number, currency: string): string {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency,
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(minor / 100);
}

/**
 * A rate, or an em dash.
 *
 * NULL IS NOT ZERO and must not render as "0%". A month with nothing decided has no win rate;
 * printing zero would say the opposite of "nothing to report". This is the whole reason the API
 * types these as nullable rather than defaulting them server-side.
 */
export function formatRate(basisPoints: number | null, digits = 1): string {
  return basisPoints === null ? '—' : `${(basisPoints / 100).toFixed(digits)}%`;
}

export function formatCount(value: number): string {
  return new Intl.NumberFormat().format(value);
}

/**
 * "3 hours ago", for the staleness line.
 *
 * Relative rather than absolute: the question a reader has is "can I trust this", and "14:20" only
 * answers it after they work out what time it is now.
 */
export function formatAge(iso: string | null): string {
  if (iso === null) return 'never refreshed';

  const minutes = Math.round((Date.now() - new Date(iso).getTime()) / 60_000);

  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;

  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;

  const days = Math.round(hours / 24);
  return `${days} ${days === 1 ? 'day' : 'days'} ago`;
}

/**
 * The month label on an x-axis: "Mar 26".
 */
export function formatMonth(iso: string): string {
  const date = new Date(`${iso}T00:00:00Z`);
  return date.toLocaleDateString(undefined, { month: 'short', year: '2-digit', timeZone: 'UTC' });
}
